import React, { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui';
import { Accordion, AccordionItem } from '../../../components/ui/Accordion';
import { Column } from '../../../components/DataTable/DataTable';
import { useClientSummary, ClientSummary } from '../useData';
import OrganizationContactsDisplay from '../../address-book/OrganizationContactsDisplay';
import ContactPointsDisplay from '../../address-book/ContactPointsDisplay';
import { getPrimaryContactPoint } from '../../address-book/contactPoints';
import { formatDate } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import type { ThemeColors } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { displayVatNumber } from '../vatUtils';
import { appointmentsCalendarPath } from '../../crm-appointments/paths';
import { todosCalendarPath } from '../../crm-todos/paths';
import { formatCurrency } from '../../../utils/formatCurrency';
import { parseStatusColorConfig, resolveStatusTextColor } from '../../../utils/statusColor';
import api from '../../../services/api';
import { getColumns as getAppointmentsColumns } from '../../crm-appointments/config';
import { getColumns as getTodosColumns } from '../../crm-todos/config';
import { getColumns as getContactsColumns } from '../../client-contacts/config';
import ClientQuickActions from './client360/ClientQuickActions';
import ClientSummaryKpis from './client360/ClientSummaryKpis';
import ClientActivityTimeline from './client360/ClientActivityTimeline';
import ClientCustomFieldsPanel from './client360/ClientCustomFieldsPanel';
import ClientRelationshipMap from './client360/ClientRelationshipMap';
import ClientAiBriefPanel from './client360/ClientAiBriefPanel';
import { emailDetailPath } from '../../emails/utils/emailPartitionAtQuery';

export interface ClientSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: number | null;
}

/** Row for read-only detail: label + value. Renders nothing when value is empty. */
function DetailRow({ label, value }: { label: string; value?: string | number | null }) {
  const { colors } = useTheme();
  const isEmpty = value === undefined || value === null || value === '';
  if (isEmpty) return null;
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-x-4 gap-y-1 py-1.5 text-sm">
      <span className={cn('font-medium', colors.textSecondary)}>{label}</span>
      <span className={colors.text}>{String(value)}</span>
    </div>
  );
}

type ClientSummaryCounts = import('../useData').ClientSummaryCounts;

/** Column definition for the records table */
interface RecordColumn {
  key: string;
  label: string;
  getValue: (record: Record<string, unknown>) => string | number | null | undefined;
  /** When set, cell is rendered as a colored status badge */
  getStatusColor?: (record: Record<string, unknown>) => string | null | undefined;
}

/** Group for related modules (display grouping in right panel) */
export type RelatedModuleGroup = 'Address book' | 'CRM' | 'Documents' | 'Finance' | 'Other';

/** Related module config: fetchUrl returns API path for records by client; columns for table display */
interface RelatedModuleConfig {
  key: string;
  label: string;
  path: string;
  countKey: keyof ClientSummaryCounts;
  group?: RelatedModuleGroup;
  fetchUrl?: (clientId: number) => string;
  getRecordId?: (record: Record<string, unknown>) => number;
  getRecordLabel?: (record: Record<string, unknown>) => string;
  detailPath?: (id: number, clientId?: number) => string;
  /** When set, overrides detailPath (e.g. emails need partitionAt from record). */
  getDetailPath?: (record: Record<string, unknown>) => string;
  /** If false, no Edit link is shown (e.g. vault folders, emails). Default true. */
  hasEdit?: boolean;
  /** Extra columns for the records table (first column is always the link/title). Ignored when getColumnsFromModule is set. */
  columns?: RecordColumn[];
  /** When set, table uses the same columns as the module list (getColumns from that module). */
  getColumnsFromModule?: (
    utils: { formatDate: (d?: string) => string; formatCurrency: (a?: number | null) => string },
    colors?: ThemeColors,
    listContext?: { mutate?: () => void }
  ) => Column<Record<string, unknown>>[];
}

/** Get display string from a relation object (e.g. { as_descr }, { pit_descr }, { pct_descr }, { tt_descr }) */
function descrFrom(rel: unknown): string | null {
  if (rel == null) return null;
  const o = rel as Record<string, unknown>;
  return (o.as_descr ?? o.pit_descr ?? o.pct_descr ?? o.pps_descr ?? o.pst_descr ?? o.pd_descr ?? o.tt_descr) as string ?? null;
}

/** Get status color from a relation object (as_color, pts_color, pst_color, tt_color, etc.) */
function colorFrom(rel: unknown): string | null {
  if (rel == null) return null;
  const o = rel as Record<string, unknown>;
  const raw = o.as_color ?? o.pts_color ?? o.pst_color ?? o.tt_color ?? o.pd_color;
  return typeof raw === 'string' ? raw : null;
}

/** Get nested value from record by key (e.g. 'client.clientName'). */
function getNestedValue(record: Record<string, unknown>, key: string): unknown {
  if (!key || !record) return undefined;
  const parts = key.split('.');
  let current: unknown = record;
  for (const p of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[p];
  }
  return current;
}

const RELATED_MODULES: RelatedModuleConfig[] = [
  {
    key: 'appointments', label: 'Appointments', path: appointmentsCalendarPath(), countKey: 'clientAppointments', group: 'CRM',
    fetchUrl: (c) => `/crm-clients-appointments/all_of/${c}`, getRecordId: (r) => (r.clientAppId as number), getRecordLabel: (r) => (r.clientAppName as string) || `#${r.clientAppId}`, detailPath: (id) => `/appointments/${id}`,
    getColumnsFromModule: (utils, colors) => getAppointmentsColumns(utils.formatDate, undefined, colors) as unknown as Column<Record<string, unknown>>[],
    columns: [
      { key: 'date', label: 'Date', getValue: (r) => r.clientAppScheduleDateTime ? formatDate(String(r.clientAppScheduleDateTime)) : null },
      { key: 'status', label: 'Status', getValue: (r) => descrFrom(r.clientAppStatusDescr), getStatusColor: (r) => colorFrom(r.clientAppStatusDescr) },
      { key: 'assigned', label: 'Assigned to', getValue: (r) => (r.assignedToUser as { userFirstName?: string; userLastName?: string }) ? [(r.assignedToUser as any).userFirstName, (r.assignedToUser as any).userLastName].filter(Boolean).join(' ') : null },
    ],
  },
  {
    key: 'todos', label: 'Todos', path: todosCalendarPath(), countKey: 'clientTodos', group: 'CRM',
    fetchUrl: (c) => `/crm-todos/all_of/${c}`, getRecordId: (r) => (r.todoId as number), getRecordLabel: (r) => (r.todoDescr as string)?.slice(0, 60) || `#${r.todoId}`, detailPath: (id) => `/todos/${id}`,
    getColumnsFromModule: (utils) => getTodosColumns(utils.formatDate) as unknown as Column<Record<string, unknown>>[],
    columns: [
      { key: 'due', label: 'Due date', getValue: (r) => r.todoDueDate ? formatDate(String(r.todoDueDate)) : null },
      { key: 'completed', label: 'Completed', getValue: (r) => r.todoCompleted ? 'Yes' : 'No' },
      { key: 'type', label: 'Type', getValue: (r) => descrFrom(r.todoType) },
    ],
  },
  {
    key: 'contacts', label: 'People', path: '/clients', countKey: 'crm_ClientsContacts', group: 'Address book',
    fetchUrl: (c) => `/client-contacts/all_of/${c}`, getRecordId: (r) => (r.clientContactId as number), getRecordLabel: (r) => {
      const name = [r.clientContactFirstName, r.clientContactLastName].filter(Boolean).join(' ').trim();
      const email = getPrimaryContactPoint((r.contactPoints as import('../../address-book/contactPoints').ContactPoint[] | undefined), 'EMAIL')?.value;
      return name || email || `#${r.clientContactId}`;
    },
    hasEdit: false,
    getColumnsFromModule: (utils) => getContactsColumns(utils.formatDate) as unknown as Column<Record<string, unknown>>[],
    columns: [
      { key: 'email', label: 'Email', getValue: (r) => getPrimaryContactPoint((r.contactPoints as import('../../address-book/contactPoints').ContactPoint[] | undefined), 'EMAIL')?.value ?? null },
      { key: 'phone', label: 'Phone', getValue: (r) => getPrimaryContactPoint((r.contactPoints as import('../../address-book/contactPoints').ContactPoint[] | undefined), 'PHONE')?.value ?? null },
      { key: 'position', label: 'Position', getValue: (r) => (r.clientContactPosition as string) ?? null },
    ],
  },
  {
    key: 'linked-emails', label: 'Linked emails', path: '/emails', countKey: 'linkedEmails', group: 'Other',
    fetchUrl: (c) => `/emails/linked_to_client/${c}`, getRecordId: (r) => (r.id as number), getRecordLabel: (r) => (r.subject as string) || (r.date ? formatDate(String(r.date)) : null) || `#${r.id}`,
    getDetailPath: (r) => emailDetailPath(r.id as number, r.partitionAt as string | undefined),
    detailPath: (id) => `/emails/${id}`,
    hasEdit: false,
    columns: [
      { key: 'date', label: 'Date', getValue: (r) => r.date ? formatDate(String(r.date)) : null },
      { key: 'from', label: 'From', getValue: (r) => { const f = r.from; if (Array.isArray(f) && f.length) { const first = f[0] as { email?: string; name?: string }; return first?.email ?? first?.name ?? null; } if (f && typeof f === 'object' && 'email' in (f as object)) return (f as { email?: string }).email ?? null; return null; } },
      { key: 'folder', label: 'Folder', getValue: (r) => (r.folder as string) ?? null },
    ],
  },
];

function toRecordList(raw: unknown): Record<string, unknown>[] {
  if (Array.isArray(raw)) return raw as Record<string, unknown>[];
  if (raw && typeof raw === 'object' && 'data' in raw && Array.isArray((raw as { data: unknown[] }).data)) return (raw as { data: Record<string, unknown>[] }).data;
  if (raw && typeof raw === 'object' && 'records' in raw && Array.isArray((raw as { records: unknown[] }).records)) return (raw as { records: Record<string, unknown>[] }).records;
  return [];
}

const MODULE_GROUP_ORDER: RelatedModuleGroup[] = ['Address book', 'CRM', 'Documents', 'Finance', 'Other'];

function RelatedModuleLinks({
  clientId,
  counts,
  singleColumn = false,
  onScrollToModule,
}: {
  clientId: number;
  counts?: ClientSummary['_count'];
  singleColumn?: boolean;
  onScrollToModule: (key: string) => void;
}) {
  const { colors } = useTheme();
  const modulesWithCount = RELATED_MODULES.filter((config) => (counts?.[config.countKey] ?? 0) > 0);
  const byGroup = MODULE_GROUP_ORDER.map((group) => ({
    group,
    configs: modulesWithCount.filter((c) => (c.group ?? 'Other') === group),
  })).filter((g) => g.configs.length > 0);

  return (
    <div className={cn('grid gap-3', singleColumn ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2')}>
      {byGroup.map(({ group, configs }) => (
        <div key={group}>
          <p className={cn('text-xs font-medium uppercase tracking-wide mb-1.5', colors.textSecondary)}>{group}</p>
          <div className="space-y-2">
            {configs.map((config) => {
              const count = counts?.[config.countKey] ?? 0;
              const listPath = config.path.startsWith('/client-vault') ? `${config.path}/${clientId}` : config.path;
              const hasFetch = !!config.fetchUrl;
              return (
                <div key={config.key} className="flex items-center gap-2">
                  {hasFetch ? (
                    <button
                      type="button"
                      onClick={() => onScrollToModule(config.key)}
                      className={cn(
                        'flex-1 flex items-center justify-between rounded-lg border px-3 py-2 text-sm text-left transition-colors',
                        colors.border,
                        colors.background,
                        'hover:opacity-90'
                      )}
                    >
                      <span className={colors.text}>{config.label}</span>
                      <span className={cn('font-medium tabular-nums', colors.textSecondary)}>{count}</span>
                    </button>
                  ) : (
                    <Link
                      to={listPath}
                      className={cn(
                        'flex-1 flex items-center justify-between rounded-lg border px-3 py-2 text-sm transition-colors',
                        colors.border,
                        colors.background,
                        'hover:opacity-90'
                      )}
                    >
                      <span className={colors.text}>{config.label}</span>
                      <span className={cn('font-medium tabular-nums', colors.textSecondary)}>{count}</span>
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

type RecordsState = { loading: boolean; error: string | null; data: Record<string, unknown>[] };

/** Shared summary body: KPIs, quick actions, accordion, sidebar, records. Used in modal and summary page. */
export function ClientSummaryBody({
  clientSummary,
  mutate,
}: {
  clientSummary: ClientSummary;
  mutate: () => void;
}) {
  const { colors } = useTheme();
  const [recordsByKey, setRecordsByKey] = useState<Record<string, RecordsState>>({});

  const loadModuleRecords = useCallback(async (config: RelatedModuleConfig, cId: number) => {
    if (!config.fetchUrl) return;
    const key = config.key;
    setRecordsByKey((prev) => ({ ...prev, [key]: { loading: true, error: null, data: [] } }));
    try {
      const url = config.fetchUrl(cId);
      const res = await api.get(url);
      const data = toRecordList(res.data);
      setRecordsByKey((prev) => ({ ...prev, [key]: { loading: false, error: null, data } }));
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'message' in err ? String((err as { message: string }).message) : 'Failed to load';
      setRecordsByKey((prev) => ({ ...prev, [key]: { loading: false, error: message, data: [] } }));
    }
  }, []);

  useEffect(() => {
    if (!clientSummary?.clientId || !clientSummary._count) return;
    const toLoad = RELATED_MODULES.filter(
      (config) => config.fetchUrl && (clientSummary._count?.[config.countKey] ?? 0) > 0
    );
    toLoad.forEach((config) => loadModuleRecords(config, clientSummary.clientId));
  }, [clientSummary?.clientId, clientSummary?._count, loadModuleRecords]);

  const handleScrollToModule = useCallback((key: string) => {
    document.getElementById(`records-${key}`)?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <div>
      <ClientQuickActions clientSummary={clientSummary} />
      <ClientSummaryKpis clientSummary={clientSummary} onScrollToModule={handleScrollToModule} />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr,minmax(0,320px)] gap-6 mb-6">
        <div className="space-y-6 min-w-0">
          <ClientAiBriefPanel clientId={clientSummary.clientId} clientName={clientSummary.clientName} />
          <ClientActivityTimeline clientId={clientSummary.clientId} />
          <ClientCustomFieldsPanel clientId={clientSummary.clientId} />
          <Accordion className="space-y-2">
              <AccordionItem title="📄 Basic information" defaultOpen={false}>
                <div className="space-y-0 pt-1">
                  <DetailRow label="Organization name" value={clientSummary.clientName} />
                  <DetailRow label="Alternate name" value={clientSummary.clientName2} />
                  <DetailRow label="Reference code" value={clientSummary.clientCode} />
                </div>
              </AccordionItem>

              <AccordionItem title="☎️ Contact" defaultOpen={false}>
                <div className="space-y-3 pt-1">
                  <div>
                    <div className={cn('text-xs font-medium mb-1', colors.textSecondary)}>Contacts</div>
                    <OrganizationContactsDisplay points={clientSummary.contactPoints} />
                  </div>
                  <div>
                    <div className={cn('text-xs font-medium mb-1', colors.textSecondary)}>Phones</div>
                    <ContactPointsDisplay points={clientSummary.contactPoints} kind="PHONE" />
                  </div>
                  <DetailRow label="Website" value={clientSummary.clientWebSite} />
                </div>
              </AccordionItem>

              <AccordionItem title="📍 Address" defaultOpen={false}>
                <div className="space-y-0 pt-1">
                  <DetailRow label="Address" value={clientSummary.clientAddress} />
                  <DetailRow label="City" value={clientSummary.clientCity} />
                  <DetailRow label="Zip code" value={clientSummary.clientZipCode} />
                  <DetailRow label="State" value={clientSummary.clientState} />
                  <DetailRow
                    label="Country"
                    value={clientSummary.clientCountryDescr?.pc_descr ?? undefined}
                  />
                </div>
              </AccordionItem>

              <AccordionItem title="💼 Business & tax" defaultOpen={false}>
                <div className="space-y-0 pt-1">
                  <DetailRow label="VAT number" value={displayVatNumber(clientSummary.clientVATNumber)} />
                  <DetailRow label="GEMI number" value={clientSummary.gemiNumber} />
                  <DetailRow label="Source URL" value={clientSummary.sourceUrl} />
                  <DetailRow label="Payment terms (days)" value={clientSummary.clientPaymentTerms} />
                  <DetailRow
                    label="Tax authority"
                    value={clientSummary.clientTaxAuthorityDescr?.pt_descr ?? undefined}
                  />
                </div>
              </AccordionItem>

          </Accordion>
        </div>

        <div className={cn('lg:sticky lg:top-4 self-start space-y-4')}>
          <ClientRelationshipMap clientSummary={clientSummary} />
          <div className={cn('rounded-xl border p-4', colors.border, colors.background)}>
            <h3 className={cn('text-sm font-semibold mb-1', colors.text)}>Jump to records</h3>
            <p className={cn('text-xs mb-3', colors.textSecondary)}>
              Scroll to detailed tables below.
            </p>
            <RelatedModuleLinks
              clientId={clientSummary.clientId}
              counts={clientSummary._count}
              singleColumn
              onScrollToModule={handleScrollToModule}
            />
          </div>
        </div>
      </div>

      {/* Records tables at the end – one per module with count > 0 */}
      <div className="mt-8 pt-6">
        <h3 className={cn('text-sm font-semibold mb-1', colors.text)}>Records</h3>
        <p className={cn('text-xs mb-4', colors.textSecondary)}>Recent items per module. Use “Jump to records” above to scroll here.</p>
      </div>
      {RELATED_MODULES.filter(
        (config) => config.fetchUrl && (clientSummary._count?.[config.countKey] ?? 0) > 0
      ).map((config) => (
        <div
          key={config.key}
          id={`records-${config.key}`}
          className={cn('mt-8 scroll-mt-4 rounded-xl border overflow-hidden', colors.border, colors.background)}
        >
          <div className={cn('flex items-center justify-between gap-3 px-4 py-3 border-b', colors.border, colors.surfaceHover)}>
            <h3 className={cn('text-base font-semibold tracking-tight', colors.text)}>
              {config.label}
            </h3>
            <Link
              to={config.path.startsWith('/client-vault') ? `${config.path}/${clientSummary.clientId}` : config.path}
              className={cn('text-sm font-medium shrink-0 rounded-md px-3 py-1.5 transition-colors', colors.textSecondary, 'hover:bg-black/5 dark:hover:bg-white/5 hover:underline')}
            >
              View full list →
            </Link>
          </div>
          {(() => {
                  const state = recordsByKey[config.key];
                  const count = clientSummary?._count?.[config.countKey] ?? 0;
                  if (!state) return null;
                  if (state.loading) {
                    return (
                      <div className="max-h-72 p-4">
                        <div className={cn('animate-pulse space-y-3', colors.background)}>
                          {[1, 2, 3, 4].map((i) => (
                            <div key={i} className={cn('h-10 rounded-lg', colors.surfaceHover)} style={{ opacity: 0.7 }} />
                          ))}
                        </div>
                      </div>
                    );
                  }
                  if (state.error) {
                    return (
                      <div className={cn('px-4 py-4 text-sm', colors.textSecondary)}>
                        <p className="text-red-600 dark:text-red-400">{state.error}</p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2"
                          onClick={() => clientSummary?.clientId && loadModuleRecords(config, clientSummary.clientId)}
                        >
                          Retry
                        </Button>
                      </div>
                    );
                  }
                  if (state.data.length === 0) {
                    const countMismatch = count > 0;
                    return (
                      <div className={cn('px-4 py-4 text-sm', colors.textSecondary)}>
                        {countMismatch
                          ? 'No records shown. Count may differ due to filters or permissions.'
                          : 'No records.'}
                      </div>
                    );
                  }
                  const getId = config.getRecordId ?? (() => 0);
                  const getLabel = config.getRecordLabel ?? (() => '—');
                  const detailPath = config.detailPath;
                  const getDetailPath = config.getDetailPath;
                  const currentClientId = clientSummary?.clientId;
                  const resolveViewPath = (record: Record<string, unknown>, recordId: number) => {
                    if (getDetailPath) return getDetailPath(record);
                    return detailPath ? detailPath(recordId, currentClientId) : '';
                  };
                  const hasDetailLink = Boolean(getDetailPath || detailPath);
                  const utils = { formatDate, formatCurrency };
                  const listContext = { mutate };
                  const moduleColumns = config.getColumnsFromModule?.(utils, colors, listContext);

                  if (moduleColumns && moduleColumns.length > 0) {
                    return (
                      <div className="max-h-72 overflow-y-auto">
                        <table className="w-full text-sm border-collapse">
                          <thead className={cn('sticky top-0 z-10', colors.surfaceHover)}>
                            <tr className={cn('border-b', colors.border)}>
                              {moduleColumns.map((col) => (
                                <th key={String(col.key)} className={cn('text-left text-xs font-semibold uppercase tracking-wider px-4 py-3 whitespace-nowrap', colors.textSecondary)}>{col.header}</th>
                              ))}
                              <th className={cn('w-10 px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider', colors.textSecondary)} aria-label="Actions" />
                            </tr>
                          </thead>
                          <tbody>
                            {state.data
                              .filter((record): record is Record<string, unknown> => record != null && typeof record === 'object')
                              .map((record: Record<string, unknown>, i: number) => {
                              const r = record;
                              const id = getId(r);
                              const viewPath = resolveViewPath(r, id as number);
                              const editPath = viewPath && !viewPath.includes('?') ? `${viewPath}/edit` : viewPath;
                              return (
                                <tr
                                  key={id ?? i}
                                  className={cn('border-b last:border-b-0', colors.border, 'hover:bg-black/[0.03] dark:hover:bg-white/[0.03]')}
                                  role={hasDetailLink && id != null ? 'button' : undefined}
                                  tabIndex={hasDetailLink && id != null ? 0 : undefined}
                                  onClick={(e) => {
                                    if (hasDetailLink && id != null && !(e.target as HTMLElement).closest('a, button')) {
                                      window.location.href = viewPath;
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    if (hasDetailLink && id != null && (e.key === 'Enter' || e.key === ' ') && !(e.target as HTMLElement).closest('a, button')) {
                                      e.preventDefault();
                                      window.location.href = viewPath;
                                    }
                                  }}
                                >
                                  {moduleColumns.map((col) => (
                                    <td key={String(col.key)} className={cn('px-4 py-2.5', colors.text)}>
                                      {r != null && col.render
                                        ? col.render(getNestedValue(r, String(col.key)), r)
                                        : String(getNestedValue(r, String(col.key)) ?? '')}
                                    </td>
                                  ))}
                                  <td className="px-4 py-2.5 w-10 align-middle text-right">
                                    {hasDetailLink && id != null && editPath && config.hasEdit !== false ? (
                                      <Link
                                        to={editPath}
                                        onClick={(e) => e.stopPropagation()}
                                        className={cn('inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors ml-auto', colors.textSecondary, 'hover:bg-black/10 dark:hover:bg-white/10')}
                                        title="Edit"
                                        aria-label="Edit"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                      </Link>
                                    ) : null}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    );
                  }

                  const cols = config.columns ?? [];
                  return (
                    <div className="max-h-72 overflow-y-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead className={cn('sticky top-0 z-10', colors.surfaceHover)}>
                          <tr className={cn('border-b', colors.border)}>
                            <th className={cn('text-left text-xs font-semibold uppercase tracking-wider px-4 py-3', colors.textSecondary)}>Title</th>
                            {cols.map((col) => (
                              <th key={col.key} className={cn('text-left text-xs font-semibold uppercase tracking-wider px-4 py-3 whitespace-nowrap', colors.textSecondary)}>{col.label}</th>
                            ))}
                            <th className={cn('w-10 px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider', colors.textSecondary)} aria-label="Actions" />
                          </tr>
                        </thead>
                        <tbody>
                          {state.data
                            .filter((record): record is Record<string, unknown> => record != null && typeof record === 'object')
                            .map((record: Record<string, unknown>, i: number) => {
                            const r = record;
                            const id = getId(r);
                            const label = getLabel(r);
                            const viewPath = resolveViewPath(r, id as number);
                            const editPath = viewPath && !viewPath.includes('?') ? `${viewPath}/edit` : viewPath;
                            return (
                              <tr key={id ?? i} className={cn('border-b last:border-b-0', colors.border, 'hover:bg-black/[0.03] dark:hover:bg-white/[0.03]')}>
                                <td className="px-4 py-2.5">
                                    {hasDetailLink && id != null ? (
                                    <Link to={viewPath} className={cn('font-medium hover:underline', colors.text)}>
                                      {label}
                                    </Link>
                                  ) : (
                                    <span className={colors.text}>{label}</span>
                                  )}
                                </td>
                                {cols.map((col) => {
                                  const value = col.getValue(r);
                                  const statusColor = col.getStatusColor?.(r);
                                  const display = value === undefined || value === null || value === '' ? '' : String(value);
                                  return (
                                    <td key={col.key} className={cn('px-4 py-2.5 whitespace-nowrap', colors.textSecondary)}>
                                      {statusColor && display ? (
                                        <span
                                          className="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium"
                                          style={{
                                            backgroundColor: parseStatusColorConfig(statusColor).backgroundColor,
                                            color: resolveStatusTextColor(parseStatusColorConfig(statusColor).backgroundColor, parseStatusColorConfig(statusColor).textColorMode),
                                          }}
                                        >
                                          {display}
                                        </span>
                                      ) : (
                                        display
                                      )}
                                    </td>
                                  );
                                })}
                                <td className="px-4 py-2.5 w-10 align-middle text-right">
                                  {hasDetailLink && id != null && editPath && config.hasEdit !== false ? (
                                    <Link
                                      to={editPath}
                                      className={cn('inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors ml-auto', colors.textSecondary, 'hover:bg-black/10 dark:hover:bg-white/10')}
                                      title="Edit"
                                      aria-label="Edit"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                      </svg>
                                    </Link>
                                  ) : null}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
        </div>
      ))}
    </div>
  );
}

export const ClientSummaryModal: React.FC<ClientSummaryModalProps> = ({
  isOpen,
  onClose,
  clientId,
}) => {
  const { clientSummary, isLoading, mutate: mutateSummary } = useClientSummary(isOpen ? clientId : null);
  const { colors } = useTheme();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalHeader>
        <ModalTitle>
          {clientSummary?.clientName ?? (clientId ? `Organization #${clientId}` : 'Organization overview')}
        </ModalTitle>
        <div className="flex items-center gap-2">
          {clientSummary && (
            <button
              type="button"
              onClick={() => mutateSummary()}
              className="p-2 rounded hover:bg-black/10 dark:hover:bg-white/10"
              title="Refresh"
              aria-label="Refresh"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-black/10 dark:hover:bg-white/10"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </ModalHeader>
      <ModalContent>
        {isLoading && (
          <div className={cn('py-8 text-center text-sm', colors.textSecondary)}>
            Loading client details…
          </div>
        )}
        {!isLoading && !clientSummary && clientId && (
          <div className={cn('py-8 text-center text-sm', colors.textSecondary)}>
            Organization not found.
          </div>
        )}
        {!isLoading && clientSummary && (
          <ClientSummaryBody clientSummary={clientSummary} mutate={mutateSummary} />
        )}
      </ModalContent>
      <ModalFooter>
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ClientSummaryModal;
